import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './UnlockedProfiles.css';

export default function UnlockedProfiles() {
  const [unlockedProfiles, setUnlockedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    fetchUnlockedProfiles();
  }, []);

  const fetchUnlockedProfiles = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCurrentUserId(null);
        setUnlockedProfiles([]);
        setLoading(false);
        return;
      }
      setCurrentUserId(user.id);

      // Get all unlocked profiles for this user with full profile details
      const { data: unlockedData, error: unlockedError } = await supabase
        .from('unlocked_profiles')
        .select(`
          profile_id,
          unlocked_at,
          profiles (
            id,
            essay,
            gpa,
            sat_score,
            act_score,
            ap_num_taken,
            ap_num_fives,
            extracurriculars,
            awards,
            class_year,
            special_program,
            college_results,
            supplemental_essays,
            rating,
            created_at,
            is_archived,
            duplicate_of
          )
        `)
        .eq('user_id', user.id)
        .order('unlocked_at', { ascending: false });

      if (unlockedError) throw unlockedError;

      // Also get user's own profiles (they should have access to these)
      const { data: ownProfiles, error: ownProfilesError } = await supabase
        .from('profiles')
        .select('*')
            .eq('is_archived', false)
        .eq('user_id', user.id);

      if (ownProfilesError) throw ownProfilesError;

      // Combine unlocked profiles and own profiles
      const allAccessibleProfiles = [
        ...unlockedData.filter(item => item.profiles && !item.profiles.is_archived).map(item => ({
          ...item.profiles,
          isOwn: false,
          unlockedAt: item.unlocked_at
        })),
        ...ownProfiles.map(profile => ({
          ...profile,
          isOwn: true,
          unlockedAt: null
        }))
      ];

      // Remove duplicates (in case own profile was also unlocked)
      const uniqueProfiles = allAccessibleProfiles.filter((profile, index, self) =>
        index === self.findIndex(p => p.id === profile.id)
      );

      setUnlockedProfiles(uniqueProfiles);
    } catch (err) {
      console.error('Error fetching unlocked profiles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStatsLine = (profile) => {
    const parts = [
      profile.gpa ? `${profile.gpa.toFixed(1)}` : null,
      profile.sat_score ? `${profile.sat_score}` : null,
      profile.act_score ? `ACT ${profile.act_score}` : null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' • ') : 'N/A';
  };

  const renderCollegeResultsSummary = (collegeResults) => {
    if (!collegeResults || typeof collegeResults !== 'object') {
      return 'No results';
    }

    const results = Object.entries(collegeResults);
    const committed = results.filter(([college, status]) => status.toLowerCase() === 'committed');
    const accepted = results.filter(([college, status]) => status.toLowerCase() === 'accepted');
    const waitlisted = results.filter(([college, status]) => status.toLowerCase() === 'waitlisted');
    // Exclude rejections from summary

    const summary = [];
    if (committed.length > 0) summary.push(`✓ ${committed[0][0]}`);
    else if (accepted.length > 0) summary.push(`${accepted.length} accepted`);
    
    if (waitlisted.length > 0) summary.push(`${waitlisted.length} waitlisted`);
    // Removed rejection count display

    const nonRejectedResults = committed.length + accepted.length + waitlisted.length;
    return summary.length > 0 ? summary.join(' • ') : (nonRejectedResults > 0 ? `${nonRejectedResults} results` : 'No acceptances');
  };

  if (loading) {
    return (
      <div className="unlocked-profiles-container">
        <div className="loading-message">
          Loading your unlocked profiles... <span className="spinner"></span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="unlocked-profiles-container">
        <div className="error-message">
          Error: {error}
          <Link to="/leaderboard" className="back-link">Back to Leaderboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="unlocked-profiles-container">
      <h2 className="page-title">Your Unlocked Profiles</h2>
      
      {!currentUserId ? (
        <div className="empty-state">
          <h3>Please log in to view unlocked profiles</h3>
          <p>Create an account or sign in to access your unlocked profiles and view your essay collection!</p>
          <Link to="/login" className="cta-button">
            Login / Sign Up
          </Link>
        </div>
      ) : unlockedProfiles.length === 0 ? (
        <div className="empty-state">
          <h3>No unlocked profiles yet</h3>
          <p>You haven't unlocked any profiles yet. Visit the leaderboard to discover and unlock profiles!</p>
          <Link to="/leaderboard" className="cta-button">
            Browse Leaderboard
          </Link>
        </div>
      ) : (
        <>
          <div className="profiles-summary">
            <p>You have access to <strong>{unlockedProfiles.length}</strong> profiles:</p>
            <ul>
              <li>{unlockedProfiles.filter(p => p.isOwn).length} of your own</li>
              <li>{unlockedProfiles.filter(p => !p.isOwn).length} unlocked from others</li>
            </ul>
          </div>

          <div className="profiles-table-container">
            <table className="profiles-table">
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>Type</th>
                  <th>Rating</th>
                  <th>Academic Stats</th>
                  <th>College Results</th>
                  <th>Essay Preview</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {unlockedProfiles.map((profile) => (
                  <tr key={profile.id} className="profile-row">
                    <td className="profile-info">
                      <div className="profile-title">
                        Class of {profile.class_year || 'N/A'}
                      </div>
                      {profile.unlockedAt && (
                        <div className="unlock-date">
                          Unlocked {new Date(profile.unlockedAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${profile.isOwn ? 'own-profile' : 'unlocked-profile'}`}>
                        {profile.isOwn ? 'Your Profile' : 'Unlocked'}
                      </span>
                    </td>
                    <td className="rating-cell">
                      <span className="rating-badge">
                        {Math.round(profile.rating || 0)}
                      </span>
                    </td>
                    <td className="stats-cell">
                      <div className="stats-line">{renderStatsLine(profile)}</div>
                      {profile.ap_num_taken && (
                        <div className="ap-info">
                          {profile.ap_num_taken} APs • {profile.ap_num_fives || 0} scored 5/7
                        </div>
                      )}
                    </td>
                    <td className="college-cell">
                      {renderCollegeResultsSummary(profile.college_results)}
                    </td>
                    <td className="essay-cell">
                      {profile.essay ? (
                        <div className="essay-preview">
                          "{profile.essay.slice(0, 80)}..."
                        </div>
                      ) : (
                        <span className="no-essay">No essay</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <Link to={`/profile/${profile.id}`} className="view-button">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="page-actions">
        <Link to="/leaderboard" className="secondary-button">
          Back to Leaderboard
        </Link>
        <Link to="/purchase-unlocks" className="primary-button">
          Get More Unlocks
        </Link>
      </div>
    </div>
  );
} 